import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

interface PaginationFooterProps {
  pageIndex: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
  showingLabel: string;
  ofLabel: string;
  pageSizeLabel?: string;
  mobileCompact?: boolean;
  previousPageLabel?: string;
  nextPageLabel?: string;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const PaginationFooter: React.FC<PaginationFooterProps> = ({
  pageIndex,
  pageSize,
  totalPages,
  totalElements,
  showingLabel,
  ofLabel,
  pageSizeLabel = 'Rows per page',
  mobileCompact = false,
  previousPageLabel = 'Previous page',
  nextPageLabel = 'Next page',
  onPageChange,
  onPageSizeChange,
}) => {
  const firstVisible = totalElements === 0 ? 0 : pageIndex * pageSize + 1;
  const lastVisible = Math.min((pageIndex + 1) * pageSize, totalElements);
  const pageCount = Math.max(1, totalPages);
  const currentPage = Math.min(Math.max(1, pageIndex + 1), pageCount);

  return (
    <>
    <Box sx={{
      display: mobileCompact ? { xs: 'none', md: 'flex' } : 'flex',
      flexDirection: mobileCompact ? { xs: 'column', md: 'row' } : { xs: 'column', sm: 'row' },
      justifyContent: 'space-between',
      alignItems: mobileCompact ? { xs: 'stretch', md: 'center' } : { xs: 'stretch', sm: 'center' },
      gap: 1.25,
      p: mobileCompact ? { xs: 1.5, md: 2 } : { xs: 1.5, sm: 2 },
      borderTop: '1px solid',
      borderColor: 'divider',
    }}>
      <Typography variant="body2" color="text.secondary">
        {showingLabel} {firstVisible} - {lastVisible} {ofLabel} {totalElements}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: mobileCompact ? { xs: '100%', md: 'auto' } : { xs: '100%', sm: 'auto' }, minWidth: 0 }}>
        <FormControl size="small" sx={{ minWidth: 76, flexShrink: 0 }}>
          <Select
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            aria-label={pageSizeLabel}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <MenuItem key={size} value={size}>{size}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Pagination
          count={pageCount}
          page={currentPage}
          onChange={(_, page) => onPageChange(page - 1)}
          color="primary"
          showFirstButton
          showLastButton
          size="small"
          siblingCount={0}
          boundaryCount={1}
        />
      </Stack>
    </Box>
    {mobileCompact && <Box sx={{
      position: 'fixed',
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: theme => theme.zIndex.appBar - 1,
      display: { xs: 'flex', md: 'none' },
      alignItems: 'center',
      justifyContent: 'space-between',
      borderTop: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      px: 2,
      pt: 1,
      pb: 'calc(8px + env(safe-area-inset-bottom))',
    }}>
      <IconButton aria-label={previousPageLabel} disabled={pageIndex <= 0} onClick={() => onPageChange(pageIndex - 1)} sx={{ width: 44, height: 44 }}>
        <ChevronLeft />
      </IconButton>
      <Typography variant="body2" color="text.secondary">
        {firstVisible}–{lastVisible} / {totalElements}
      </Typography>
      <IconButton aria-label={nextPageLabel} disabled={currentPage >= pageCount || totalElements === 0} onClick={() => onPageChange(pageIndex + 1)} sx={{ width: 44, height: 44 }}>
        <ChevronRight />
      </IconButton>
    </Box>}
    </>
  );
};

export default PaginationFooter;
